import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, List, MessageSquare, Package, Wrench, Calculator, Truck, Clock, Percent, ArrowRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { setApiEmployer } from "../../lib/api";
import { rememberEmployerContext } from "../../lib/employerModules";
import CompanyListings from "./CompanyListings";
import CompanyModulePanel from "./CompanyModulePanel";
import CompanyMyListPanel from "./CompanyMyListPanel";
import CompanyKpiBar from "./CompanyKpiBar";
import CompanyTasksPanel from "./CompanyTasksPanel";
import SellerCommissionsCard from "../dashboard/SellerCommissionsCard";
import EmployeeTimeCard from "../dashboard/EmployeeTimeCard";

const MODULES = [
    { key: "inquiries", label: "Anfragen", Icon: MessageSquare },
];

/**
 * CompanyWorkspacePanel — Firmen-Arbeitsbereich eines bestätigten Mitarbeiters.
 * Phase 3b: freigegebene Module (employer.permissions) sind aktiv & anklickbar;
 * das gewählte Modul zeigt seine echten Firmendaten read-only an. Gesperrte
 * Module bleiben grau. KPI-Leiste oben für Handlungsbedarf auf einen Blick.
 */
export default function CompanyWorkspacePanel({ employer, initialModule }) {
    const navigate = useNavigate();
    const perms = employer?.permissions || [];
    const granted = MODULES.filter((m) => perms.includes(m.key));
    const [active, setActive] = useState(() => {
        if (initialModule && perms.includes(initialModule)) return initialModule;
        return (granted.find((m) => m.key === "listings") || granted[0])?.key || null;
    });
    const [pendingModules, setPendingModules] = useState([]);
    const [requesting, setRequesting] = useState(null);
    const [commOpen, setCommOpen] = useState(initialModule === "provisionen");
    const [kpis, setKpis] = useState([]);
    const commissionRef = useRef(null);

    const dealerId = employer?.dealer_id;
    const perms2 = employer?.permissions || [];
    // Jul-2026 (User-Wunsch) — identische Anordnung wie im Mitarbeiter-Login
    // („Aufgaben & Hinweise"): Aufgaben → Leads → Verkäufe → Werkstattaufträge
    // → Transporte → Anfragen. Kein Bereich geht verloren.
    const TABS = [
        { key: "my_tasks", label: "Meine Aufgaben", kpi: "my_tasks" },
        { key: "my_leads", label: "Meine Leads", kpi: "my_leads", scope: "leads" },
        { key: "my_sales", label: "Meine Verkäufe", kpi: "my_sales", scope: "sales" },
        { key: "my_workshop", label: "Meine Werkstattaufträge", kpi: "my_workshop", scope: "workshop" },
        { key: "my_transports", label: "Offene Transporte", kpi: "my_transports", scope: "transports" },
        ...(perms2.includes("inquiries") ? [{ key: "inquiries", label: "Anfragen", kpi: null }] : []),
    ];
    const [tab, setTab] = useState(() => {
        if (initialModule && TABS.some((t) => t.key === initialModule)) return initialModule;
        return TABS[0]?.key || null;
    });
    const kpiVal = (m) => kpis.find((k) => k.module === m)?.value;

    useEffect(() => {
        if (!dealerId) return;
        let on = true;
        api.get(`/vermittler/company/${dealerId}/kpis`)
            .then((r) => { if (on) setKpis(r.data.kpis || []); })
            .catch(() => { if (on) setKpis([]); });
        return () => { on = false; };
    }, [dealerId]);

    useEffect(() => {
        if (initialModule === "provisionen" && commissionRef.current) {
            setCommOpen(true);
            commissionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [initialModule, dealerId]);
    useEffect(() => {
        if (!dealerId) return;
        let alive = true;
        api.get(`/vermittler/company/${dealerId}/my-access-requests`)
            .then((r) => { if (alive) setPendingModules(r.data.pending_modules || []); })
            .catch(() => { if (alive) setPendingModules([]); });
        return () => { alive = false; };
    }, [dealerId]);

    const requestAccess = async (moduleKey) => {
        setRequesting(moduleKey);
        try {
            await api.post(`/vermittler/company/${dealerId}/request-access`, { module: moduleKey });
            setPendingModules((p) => (p.includes(moduleKey) ? p : [...p, moduleKey]));
            toast.success("Freigabe beim Arbeitgeber angefragt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        } finally {
            setRequesting(null);
        }
    };

    if (!employer) return null;

    // active validieren (falls Rechte sich änderten)
    const activeKey = granted.some((m) => m.key === active) ? active : (granted[0]?.key || null);

    // P1 Werkstatt (Jun-2026): das ECHTE Händler-Modul im Arbeitgeber-Kontext
    // öffnen (statt der generischen Read-Only-Tabelle).
    const openRealModule = (path) => {
        setApiEmployer(employer.dealer_id);
        rememberEmployerContext(employer);
        navigate(path);
    };
    const REAL_MODULE_ROUTES = {
        workshop: { path: "/dealer/workshop-orders", label: "Werkstatt-Aufträge" },
        wms: { path: "/dealer/wms", label: "Warenwirtschaft" },
        dispo: { path: "/dealer/disposition", label: "Disposition" },
        accounting: { path: "/dealer/buchhaltung", label: "Buchhaltung" },
    };

    // KPI-Kachel-Klick (User-Wunsch): direkt in die echte Modulseite, vorgefiltert
    // auf die EIGENEN Vorgänge des Verkäufers (sofern möglich).
    const onKpiSelect = (k) => {
        const hasPerm = (p) => (employer.permissions || []).includes(p);
        if (k.module === "my_workshop" && hasPerm("workshop")) {
            return openRealModule("/dealer/workshop-orders?mine=1&status=open");
        }
        if ((k.module === "my_sales" || k.module === "my_transports") && hasPerm("dispo")) {
            return openRealModule("/dealer/disposition");
        }
        // „Meine Leads" (User-Wunsch): direkt in die Lead-/Anfragen-Ansicht des
        // Arbeitgebers (Employer-Kontext). Ohne inquiries-Freigabe Fallback auf
        // das generische read-only Anfragen-Modul.
        if (k.module === "my_leads") {
            if (hasPerm("inquiries")) return openRealModule("/dealer/leads?mine=1");
            return setActive("inquiries");
        }
        // Fallback: passendes Modul im generischen Workspace anwählen.
        setActive(k.target || k.module);
    };

    return (
        <div className="space-y-4" data-testid="company-workspace-panel">
            <div className="bg-[#111827] text-white rounded-sm p-6 flex items-center gap-4" data-testid="company-workspace-header">
                <div className="w-14 h-14 rounded-sm bg-[#FFCC00] text-[#111827] flex items-center justify-center font-display font-black text-2xl shrink-0">
                    {(employer.dealer_name || "?").charAt(0)}
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display text-xl font-bold truncate" data-testid="company-workspace-name">{employer.dealer_name}</h2>
                        <span className="text-[10px] uppercase tracking-wider font-bold bg-[#FFCC00] text-[#111827] px-1.5 py-0.5 rounded-sm">Mitarbeiter</span>
                    </div>
                    <p className="text-xs text-gray-300 mt-0.5">
                        Firmen-Arbeitsbereich · {perms.length > 0 ? `${perms.length} Bereich(e) freigegeben` : "Zugriff wird vom Arbeitgeber freigegeben"}.
                    </p>
                </div>
            </div>

            {perms.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-start gap-3" data-testid="company-workspace-pending">
                    <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0" />
                    <div className="text-sm text-gray-700">
                        Ihr Arbeitgeber legt unter <strong>Einstellungen → Mitarbeiter</strong> fest,
                        welche Bereiche Sie hier sehen dürfen. Sobald Bereiche freigegeben sind,
                        erscheinen sie an dieser Stelle.
                    </div>
                </div>
            )}

            {/* Jul-2026 (User-Wunsch) — „Aufgaben & Hinweise" identisch & an der
                gleichen Position wie im Mitarbeiter-Login (ganz oben). */}
            <h2 className="font-display font-black text-xl text-[#111827]" data-testid="company-tasks-heading">Aufgaben &amp; Hinweise</h2>
            {/* Firma-Tabs (User-Wunsch Jun-2026): Anfragen + eigene Vorgänge je als
                eigener Tab; Inhalt inline, Klick auf eine Zeile öffnet den Vorgang. */}
            <div className="bg-white border border-gray-200 rounded-sm" data-testid="company-tabs-wrap">
                <div className="flex gap-1 overflow-x-auto border-b border-gray-200 px-2 pt-2" data-testid="company-tabs">
                    {TABS.map((t) => {
                        const cnt = kpiVal(t.kpi);
                        const isActive = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={`shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-t-sm border-b-2 transition-colors ${
                                    isActive ? "border-[#FFCC00] text-[#111827]" : "border-transparent text-gray-500 hover:text-[#111827]"
                                }`}
                                data-testid={`company-tab-${t.key}`}
                            >
                                {t.label}
                                {cnt != null && (
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cnt > 0 ? "bg-[#FFCC00] text-[#111827]" : "bg-gray-100 text-gray-500"}`}>{cnt}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <div className="p-3" data-testid={`company-tab-content-${tab}`}>
                    {tab === "inquiries" && <CompanyModulePanel dealerId={employer.dealer_id} module="inquiries" />}
                    {tab === "my_tasks" && <CompanyTasksPanel dealerId={employer.dealer_id} />}
                    {["my_sales", "my_workshop", "my_transports", "my_leads"].includes(tab) && (
                        <CompanyMyListPanel
                            dealerId={employer.dealer_id}
                            scope={TABS.find((t) => t.key === tab)?.scope}
                            onOpen={openRealModule}
                        />
                    )}
                </div>
            </div>

            {/* Arbeitszeiterfassung des Mitarbeiters bei diesem Arbeitgeber
                (Stempeluhr, Konten, Urlaub/Frei/Krank-Anträge) — User-Wunsch Jun-2026. */}
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="company-time-section">
                <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-[#FFCC00]" />
                    <h3 className="font-display font-bold text-sm">Meine Zeiterfassung bei {employer.dealer_name}</h3>
                </div>
                <EmployeeTimeCard employerId={employer.dealer_id} />
            </div>

            {/* Provisionsabrechnung ganz unten & einklappbar (User-Wunsch Jun-2026). */}
            <div className="bg-white border border-gray-200 rounded-sm" data-testid="company-commissions-section" ref={commissionRef}>
                <button
                    type="button"
                    onClick={() => setCommOpen((o) => !o)}
                    className="w-full flex items-center gap-2 p-4 text-left hover:bg-gray-50 transition-colors"
                    data-testid="company-commissions-toggle"
                    aria-expanded={commOpen}
                >
                    <Percent className="w-4 h-4 text-[#FFCC00] shrink-0" />
                    <h3 className="font-display font-bold text-sm">Meine Provisionen bei {employer.dealer_name}</h3>
                    <ChevronDown className={`w-4 h-4 ml-auto text-gray-400 transition-transform ${commOpen ? "rotate-180" : ""}`} />
                </button>
                {commOpen && (
                    <div className="px-4 pb-4" data-testid="company-commissions-body">
                        <SellerCommissionsCard dealerId={employer.dealer_id} />
                    </div>
                )}
            </div>
        </div>
    );
}
